import React, {
    ReactElement, ReactNode, useContext, useMemo,
} from "react";
import {
    Link as RouterLink, NavLink as RouterNavLink, useNavigate, useMatch as useBaseRouteMatch,
    Navigate as BaseNavigate,
} from "react-router-dom";

export const Paths = {
    ACCOUNT_CREATE_ID: "/accounts/create/:inviteId",
    ACCOUNT_CREATE: "/accounts/create",
    ACCOUNT_PASSWORD: "/accounts/password",
    ACCOUNT_PROFLE: "/profile",

    ANSWERS_NEW: "/answers/new",
    ANSWERS_GET: "/answers/:id",
    ANSWERS_LIST: "/answers",

    LOGIN: "/login",
    LOGIN_WITH_TOKEN: "/login/token/:token",
    LOGIN_RESET: "/login/password-reset",

    CHANNEL_INVITE: "/channel-invite/:id",

    AGENT: "/agent",
    AGENT_COMPOSE: "/agent/new",
    AGENT_CHAT: "/agent/:chatId",

    TICKETS: "/tickets",
    TICKETS_CREATE: "/tickets/new",
    TICKETS_VIEW: "/tickets/:ticketId",

    CONTACTS: "/contacts",
    CONTACTS_ENTITY: "/contacts/:entity",
    ORGANIZATIONS_ID: "/contacts/organizations/:id",
    ORGANIZATIONS_NEW: "/contacts/organizations/new",
    ORGANIZATION_USERS: "/contacts/organizations/:companyId/users",
    USERS_ID: "/contacts/users/:id",
    USERS_NEW: "/contacts/users/new",

    MANAGE_USERS: "/users",
    HOME: "/",
    ADMINISTRATOR: "/admin",
    ADMINISTRATOR_SETTING: "/admin/:setting",
    ADMINISTRATOR_SETTING_ID: "/admin/:setting/:id",

    SITE_ADMINISTRATOR: "/site-admin",
    SITE_ADMINISTRATOR_SETTING: "/site-admin/:setting",
    SITE_ADMINISTRATOR_SETTING_ID: "/site-admin/:setting/:id",

    ONBOARDING: "/onboarding",
    ONBOARDING_STEP: "/onboarding/:stepId",

    ERROR_404: "/errors/404",
    ERROR_INTERNAL: "/errors/internal",
} as const;

export interface Params extends Record<PathValues, Record<string, string>> {
    [Paths.ANSWERS_NEW]: {
        tagId: string
    },
    [Paths.AGENT]: {
        search: string
    },
    [Paths.AGENT_CHAT]: {
        search: string
    },
    [Paths.ADMINISTRATOR_SETTING_ID]: {
        error: string,
        isSuccess: string,
        feature: string;
    },
    [Paths.HOME]: {
        redirect: string,
        isWaitLogin: string
    },
    [Paths.LOGIN]: {
        error: string,
        redirect: string;
    },
    [Paths.TICKETS]: {
        requestTypeId: string
    },
    [Paths.USERS_NEW]: {
        companyId: string
    },
}

type ExtractParams<T> =
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    T extends `${infer _Start}:${infer Param}/${infer Rest}` ? { [k in Param | keyof ExtractParams<Rest>]: string } :
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // eslint-disable-next-line @typescript-eslint/indent
    T extends `${infer _Start}:${infer Param}` ? { [k in Param]: string } : never;

export type DynamicParameter<T> = [T] extends [never] ? [] : [T];

export type PathValues = (typeof Paths)[keyof typeof Paths];

export const buildUrl = <P extends PathValues>(
    path: P,
    ...params: DynamicParameter<ExtractParams<P>>
): string => {
    let ret: string = path;

    if (params.isNotEmpty) {
        const paramObj: Record<string, string> = params.firstElement;
        if (paramObj) {
            Object.entries(paramObj).forEach(([key, value]) => {
                ret = ret.replace(`:${key}`, value);
            });
        }
    }

    return ret;
};

const EMPTY = Object.create(null);

export enum PathMatchType {
    FULL_PATH,
    PARTIAL_PATH
}

export const useParams = <P extends PathValues>(
    path: P, matchType = PathMatchType.FULL_PATH,
): ExtractParams<P> => {
    // `exact`, `sensitive` and `strict` options are set to true
    // to ensure type safety.
    const match = useBaseRouteMatch({
        path,
        caseSensitive: true,
        end: matchType === PathMatchType.FULL_PATH,
    });

    if (!match) {
        return EMPTY;
    }
    return match.params as ExtractParams<P> ?? {};
};

export const isCurrentRoute = <P extends PathValues>(
    path: P,
): boolean => {
    // `exact`, `sensitive` and `strict` options are set to true
    // to ensure type safety.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const match = useBaseRouteMatch({
        path,
        caseSensitive: true,
    });
    return match != null;
};

export const isRouteMatch = <P extends PathValues>({
    path,
}: {
    path: P;
    exact?: boolean;
    strict?: boolean;
    sensitive?: boolean;
},

): boolean => {
    // `exact`, `sensitive` and `strict` options are set to true
    // to ensure type safety.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const match = useBaseRouteMatch({
        path,
        caseSensitive: true,
    });

    return match != null;
};

const Routing = {
    Paths,
    buildUrl,
};

export interface RoutingProps {
    Paths: typeof Paths;
    buildUrl: typeof buildUrl;
    historyPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => void;
    historyPushWithSearch: <P extends PathValues>(
        path: P, searchParams: Params[P], ...params: DynamicParameter<ExtractParams<P>>
    ) => void;
    historyPushSearch: <P extends PathValues>(searchParams: Params[P]) => void;
    createHistoryPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => (() => void);
    redirect: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => void;
    getSearchParams: <P extends PathValues>(path: P) => Params[P];
}

export const RoutingContext = React.createContext<RoutingProps>({
    Paths,
    buildUrl,
    historyPush: () => { /* default */ },
    historyPushWithSearch: () => { /* default */ },
    historyPushSearch: () => { /* default */ },
    createHistoryPush: () => () => { /* default */ },
    redirect: () => { /* default */ },
    getSearchParams: () => Object.create(null),
});

function parseParams(searchParams: Record<string, string>): URLSearchParams {
    const params = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach(child => params.append(key, child));
        } else {
            params.append(key, value);
        }
    });
    return params;
}
export function RoutingProvider({ children }: { children: ReactElement }): (ReactElement | null) {
    const navigate = useNavigate();

    const current = useMemo(() => ({
        ...Routing,
        historyPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => {
            const url = buildUrl(path, ...params);
            navigate(url);
        },
        historyPushWithSearch: <P extends PathValues>(
            path: P, searchParams: Record<string, string>, ...params: DynamicParameter<ExtractParams<P>>
        ) => {
            const url = buildUrl(path, ...params);
            const search = parseParams(searchParams).toString();
            navigate({
                pathname: url,
                search,
                // search: parseParams(searchParams).toString(),
            });
        },
        historyPushSearch: (searchParams: Record<string, string>) => {
            navigate({
                pathname: window.location.pathname,
                search: parseParams(searchParams).toString(),
            });
        },
        createHistoryPush: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => {
            const url = buildUrl(path, ...params);
            return () => {
                navigate(url);
            };
        },
        redirect: <P extends PathValues>(path: P, ...params: DynamicParameter<ExtractParams<P>>) => {
            const url = buildUrl(path, ...params);
            window.location.href = url;
        },

        getSearchParams() {
            const obj = Object.create(null);
            let isDirty = false;
            new URLSearchParams(window.location.search).forEach((value, key) => {
                let curr = obj[key];
                if (curr == null) {
                    if (value != null && value !== "") {
                        isDirty = true;
                        obj[key] = value;
                    }
                    return;
                }
                if (!Array.isArray(curr)) {
                    curr = [curr];
                    isDirty = true;
                    obj[key] = curr;
                }
                if (Array.isArray(curr)) {
                    curr.push(value);
                }
            });
            return isDirty ? obj : {};
        },
    }), []);
    return (
        <RoutingContext.Provider value={current}>
            {children}
        </RoutingContext.Provider>
    );
}

export default function useRouting(): RoutingProps {
    return useContext(RoutingContext) ?? {};
}

type TypedLinkProps<P extends PathValues, SearchParams extends Params[P] = Params[P]> = {
    to: P,
    className?: string,
    replace?: boolean,
    children?: ReactNode,
    tabIndex?: number;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    linkRef?: React.RefObject<HTMLAnchorElement>
    onDragCapture?: React.DragEventHandler<HTMLAnchorElement>;
    search?: SearchParams extends never ? Record<string, string> : Partial<SearchParams>;
    target?: string;
    rel?: string;
};

type ConditionalParams<P extends PathValues> =
    // eslint-disable-next-line @typescript-eslint/ban-types
    TypedLinkProps<P> & (ExtractParams<P> extends never ? {} : { params: ExtractParams<P> });

function hasValues(obj: Record<string, unknown>): boolean {
    return Object.values(obj).filter(row => row != null && row !== "").isNotEmpty;
}
export function Link<P extends PathValues>(params: ConditionalParams<P>): ReactElement {
    const children = [(params as unknown as ({ params: ExtractParams<P> }))?.params];
    return (
        <RouterLink
            to={{
                pathname: buildUrl(params.to, ...children as DynamicParameter<ExtractParams<P>>),
                search: params.search && hasValues(params.search)
                    ? new URLSearchParams(params.search as Record<string, string>).toString()
                    : undefined,
            }}
            replace={params.replace}
            className={params.className}
            onClick={params.onClick}
            tabIndex={params.tabIndex}
            ref={params.linkRef}
            target={params.target}
            rel={params.rel}
        >
            {params.children}
        </RouterLink>
    );
}

export function NavLink<P extends PathValues>(params: ConditionalParams<P>): ReactElement {
    const children = [(params as unknown as ({ params: ExtractParams<P> }))?.params];
    return (
        <RouterNavLink
            to={buildUrl(params.to, ...children as DynamicParameter<ExtractParams<P>>)}
            replace={params.replace}
            className={params.className}
            onClick={params.onClick}
            tabIndex={params.tabIndex}
            ref={params.linkRef}
            target={params.target}
            rel={params.rel}
        >
            {params.children}
        </RouterNavLink>
    );
}

export function Navigate<P extends PathValues>(params: ConditionalParams<P>): ReactElement {
    const children = [(params as unknown as ({ params: ExtractParams<P> }))?.params];
    return (
        <BaseNavigate to={{
            pathname: buildUrl(params.to, ...children as DynamicParameter<ExtractParams<P>>),
            search: params.search && hasValues(params.search)
                ? new URLSearchParams(params.search as Record<string, string>).toString()
                : undefined,
        }}
        />
    );
}
